import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Trash2, Play, Download, Satellite, Waves } from "lucide-react";
import { toast } from "sonner";
import { useSim } from "../lib/store";
import { listScenarios, saveScenario, deleteScenario, fetchUsgsRecent } from "../lib/api";

export function ScenarioDrawer() {
  const open = useSim((s) => s.drawerOpen);
  const setOpen = useSim((s) => s.setDrawerOpen);
  const loadScenario = useSim((s) => s.loadScenario);
  const state = useSim();

  const [scenarios, setScenarios] = useState([]);
  const [usgs, setUsgs] = useState([]);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [tab, setTab] = useState("saved");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      setScenarios(await listScenarios());
    } catch {
      toast.error("Could not load saved scenarios");
    }
  };

  useEffect(() => {
    if (open) {
      setTab("saved");
      refresh();
    }
  }, [open]);

  const handleSave = async () => {
    if (!state.epicenter) return toast.error("Drop an epicenter pin before saving");
    if (!name.trim()) return toast.error("Give this scenario a name");
    try {
      await saveScenario({
        name: name.trim(),
        notes,
        magnitude: state.magnitude,
        duration: state.duration,
        depth: state.depth,
        soil_type: state.soilType,
        location_label: `${state.epicenter.lat.toFixed(2)}, ${state.epicenter.lon.toFixed(2)}`,
        epicenter: { lat: state.epicenter.lat, lon: state.epicenter.lon, offshore: state.epicenter.offshore },
      });
      toast.success("Scenario archived");
      setName(""); setNotes("");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg ? `Save failed: ${msg}` : "Save failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteScenario(id);
      setScenarios((s) => s.filter((x) => x.id !== id));
      toast("Scenario removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const importUsgs = async () => {
    setLoading(true);
    try {
      const data = await fetchUsgsRecent();
      setUsgs(data.events || []);
      setTab("usgs");
      toast.success(`Loaded ${data.events?.length || 0} events (${data.source})`);
    } catch {
      toast.error("USGS fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const loadUsgs = (ev) => {
    useSim.getState().loadScenario({
      magnitude: Math.min(9.5, Math.max(4, ev.magnitude || 6)),
      duration: Math.min(180, Math.max(10, Math.round((ev.magnitude || 6) * 12))),
      depth: ev.depth || 10,
      soil_type: "dense_sand",
      epicenter: { lat: ev.lat, lon: ev.lon, offshore: ev.tsunami || false },
    });
    toast.success(`Loaded ${ev.place || "USGS event"}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="scenario-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full sm:w-[440px] bg-zinc-950/95 backdrop-blur-3xl border-l border-white/15 p-5 flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold text-white uppercase tracking-tight">Scenario Library</h2>
              <button data-testid="close-drawer-button" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* save form */}
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-300 mb-2">Archive Current Rupture</h3>
              <input
                data-testid="scenario-name-input"
                value={name} onChange={(e) => setName(e.target.value)} placeholder="Scenario name"
                className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-red-500/50 focus:outline-none mb-2"
              />
              <textarea
                data-testid="scenario-notes-input"
                value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Investigative notes…" rows={2}
                className="w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-red-500/50 focus:outline-none mb-3 resize-none"
              />
              <button
                data-testid="save-scenario-button"
                onClick={handleSave}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold text-sm shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:from-red-500 hover:to-red-600 transition-all"
              >
                <Save className="h-4 w-4" /> Save Scenario
              </button>
            </div>

            {/* tabs */}
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setTab("saved")} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${tab === "saved" ? "bg-red-600/90 text-white" : "bg-white/5 text-zinc-400"}`}>Saved</button>
              <button data-testid="usgs-import-button" onClick={importUsgs} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${tab === "usgs" ? "bg-red-600/90 text-white" : "bg-white/5 text-zinc-400"}`}>
                <Satellite className="h-3.5 w-3.5" /> {loading ? "Loading…" : "USGS Live"}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-2">
              {tab === "saved" &&
                (scenarios.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-8">No saved scenarios yet.</p>
                ) : (
                  scenarios.map((s) => (
                    <div key={s.id} data-testid={`scenario-item-${s.id}`} className="rounded-xl bg-white/5 border border-white/10 hover:border-red-500/30 p-3 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-white truncate">{s.name}</div>
                          <div className="font-mono-tele text-[11px] text-red-400 mt-0.5">Mw {s.magnitude.toFixed(1)} · {s.duration}s{s.location_label ? ` · ${s.location_label}` : ""}</div>
                          {s.notes && <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{s.notes}</div>}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button data-testid={`load-scenario-${s.id}`} onClick={() => { loadScenario(s); toast.success(`Loaded ${s.name}`); }} className="p-1.5 rounded-lg bg-red-600/20 text-red-300 hover:bg-red-600/40"><Play className="h-3.5 w-3.5" /></button>
                          <button data-testid={`delete-scenario-${s.id}`} onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg bg-white/5 text-zinc-400 hover:bg-red-600/20 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))
                ))}

              {tab === "usgs" &&
                (usgs.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-8">Tap "USGS Live" to import recent significant earthquakes.</p>
                ) : (
                  usgs.map((ev) => (
                    <button key={ev.id} onClick={() => loadUsgs(ev)} className="w-full text-left rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/40 p-3 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white truncate">{ev.place || "Unknown"}</div>
                          <div className="font-mono-tele text-[11px] text-zinc-400 mt-0.5">{ev.lat?.toFixed(2)}, {ev.lon?.toFixed(2)} · {ev.depth?.toFixed(0)} km</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {ev.tsunami && <Waves className="h-3.5 w-3.5 text-cyan-400" />}
                          <span className="font-mono-tele text-base font-bold text-red-500">{ev.magnitude?.toFixed(1)}</span>
                        </div>
                      </div>
                    </button>
                  ))
                ))}
            </div>

            <div className="pt-3 mt-3 border-t border-white/10">
              <button
                data-testid="export-json-button"
                onClick={() => {
                  const st = useSim.getState();
                  const payload = { magnitude: st.magnitude, duration: st.duration, depth: st.depth, soil: st.soilType, epicenter: st.epicenter };
                  navigator.clipboard?.writeText(JSON.stringify(payload, null, 2));
                  toast.success("Telemetry JSON copied to clipboard");
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-300 text-xs font-medium border border-white/10 uppercase tracking-wider"
              >
                <Download className="h-4 w-4" /> Export Telemetry JSON
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
